import { useAsyncData, useRoute } from '#imports'
import { ref, watch } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  reviewsConfig,
  reviewsRequestEndpoints,
} from '~/scopes/reviews/reviews-display/constants'

import { transformReviewsResponse } from '../utils/transformReviewsResponse'

function useFetchReviewsWithAttachments() {
  const route = useRoute()
  const locale = useI18nLocale()
  const { productId } = useUrlParams()
  const $httpFetch = useHttpFetch()

  const minRate = 4.7
  const maxAmount = 20
  const withAttachments = true

  const uuid: string = route.params?.uuid ? route.params.uuid : productId
  const routeName = route.name?.toString() ?? ''
  const endpoint = reviewsRequestEndpoints?.[routeName]?.getReviews
  if (!endpoint)
    return {
      data: ref(),
      error: ref(),
      pending: ref(false),
      execute: () => Promise.resolve(),
    }

  return useAsyncData(
    getAsyncDataKey('reviews-with-attachments', {
      uuid,
      locale,
      routeName,
    }),
    () => {
      const currentRouteName = route.name?.toString() ?? ''
      const headers = reviewsConfig?.[currentRouteName]?.headers
      const reviewsListQuery =
        reviewsConfig?.[currentRouteName]?.reviewsListQuery

      return $httpFetch(endpoint, {
        headers,
        pathParams: { uuid },
        query: {
          ...reviewsListQuery,
          order_by: '-relevance',
          global_rate__gte: minRate,
          page_size: maxAmount,
          with_attachments: withAttachments,
          translation_locale: locale,
        },
      })
    },
    {
      server: false,
      transform: ({ results }) => transformReviewsResponse(results),
      lazy: true,
    },
  )
}

export function useReviewsWithAttachments() {
  const route = useRoute()
  const logger = useLogger()

  const { data, error, pending, execute } = useFetchReviewsWithAttachments()

  watch(
    error,
    () => {
      if (error.value instanceof Error) {
        logger.error(`[REVIEWS][${route.name?.toString()}] With attachments`, {
          error: error.value,
          owners: ['bot-squad-content-front'],
        })
      }
    },
    { immediate: true },
  )

  watch(
    () => [route.params, route.name],
    async () => {
      await execute()
    },
  )

  return {
    reviewsWithAttachments: data,
    isPendingReviewsWithAttachments: pending,
  }
}
